/**
 * Inventory Analytics Page
 * Stock insights from SQL Accounting
 */
import { useState, useEffect } from 'react';
import { 
  Package, AlertTriangle, TrendingUp, RefreshCw, DollarSign,
  AlertCircle, CheckCircle2, Loader2, BarChart3
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InventoryAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/analytics/inventory`, { 
        withCredentials: true 
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching inventory analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API_URL}/api/sql-acc/sync/stock`, {}, { 
        withCredentials: true 
      });
      await fetchData();
    } catch (error) {
      console.error('Error syncing:', error);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 text-red-700 animate-spin" />
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ms-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="inventory-analytics-page">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
              <Package className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Inventory Analytics</h1>
              <p className="text-gray-500">Stock insights dari SQL Accounting</p>
            </div>
          </div>
          
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from SQL'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-5 h-5 text-purple-500" />
            <span className="text-gray-500 text-sm">Total Items</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{data?.total_items || 0}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-5 h-5 text-blue-500" />
            <span className="text-gray-500 text-sm">Stock Value (Cost)</span>
          </div>
          <p className="text-3xl font-bold text-blue-600">{formatCurrency(data?.total_cost_value)}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            <span className="text-gray-500 text-sm">Retail Value</span>
          </div>
          <p className="text-3xl font-bold text-emerald-600">{formatCurrency(data?.total_retail_value)}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            <span className="text-gray-500 text-sm">Potential Profit</span>
          </div>
          <p className="text-3xl font-bold text-amber-600">{formatCurrency(data?.potential_profit)}</p>
        </div>
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Low Stock Alert */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800">Low Stock Alert</h2>
              <p className="text-sm text-gray-500">{data?.low_stock_count || 0} items below reorder level</p>
            </div>
          </div>
          
          {data?.low_stock_items?.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data.low_stock_items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">{item.description}</p>
                    <p className="text-xs text-gray-500">{item.sql_code}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-amber-600">{item.qty_on_hand} {item.uom}</p>
                    <p className="text-xs text-gray-500">Reorder: {item.reorder_level}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="w-5 h-5" />
              <span>All items above reorder level</span>
            </div>
          )}
        </div>

        {/* Out of Stock */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800">Out of Stock</h2>
              <p className="text-sm text-gray-500">{data?.out_of_stock_count || 0} items with zero quantity</p>
            </div>
          </div>
          
          {data?.out_of_stock_items?.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data.out_of_stock_items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">{item.description}</p>
                    <p className="text-xs text-gray-500">{item.sql_code}</p>
                  </div>
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded">
                    OUT OF STOCK
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="w-5 h-5" />
              <span>No out of stock items</span>
            </div>
          )}
        </div>
      </div>

      {/* Stock by Group */}
      {data?.by_group && Object.keys(data.by_group).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Stock by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data.by_group).map(([group, info]) => (
              <div key={group} className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800">{group || 'Uncategorized'}</p>
                <p className="text-2xl font-bold text-purple-600">{info.count}</p>
                <p className="text-sm text-gray-500">{formatCurrency(info.value)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data?.message && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-amber-700">{data.message}</p>
        </div>
      )}

      {data?.synced_at && (
        <p className="text-xs text-gray-400 text-center">
          Last synced: {new Date(data.synced_at).toLocaleString('ms-MY')}
        </p>
      )}
    </div>
  );
};

export default InventoryAnalyticsPage;
